import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import type { ResourceCtaType, ResourceLanguage } from '../content/resources'
import { Button } from '@/components/ui/button'
import { trackClickRequestDemo, trackClickSignup } from '@/shared/analytics'
import { cn } from '@/shared/lib/utils'

type CtaContent = {
  title: string
  body: string
  buttonLabel: string
  to: '/$lang/signup' | '/$lang/demo' | '/$lang'
}

const CTA_CONTENT: Record<
  ResourceLanguage,
  Record<ResourceCtaType, CtaContent>
> = {
  en: {
    'context-audit': {
      title: 'Discover where your team is losing context',
      body: 'Connect your workspace and identify fragmented, outdated, or missing context.',
      buttonLabel: 'Run a Context Audit',
      to: '/$lang/signup',
    },
    'build-agent': {
      title: 'Turn this workflow into a managed AI agent',
      body: 'Build an agent with the right instructions, context, tools, and approval boundaries.',
      buttonLabel: 'Build your agent',
      to: '/$lang/signup',
    },
    'book-demo': {
      title: 'See how Laplace fits your business processes',
      body: 'Explore managed AI agents with your own workflows and systems.',
      buttonLabel: 'Book a demo',
      to: '/$lang/demo',
    },
    'find-customers': {
      title: 'See who could buy from you',
      body: 'Paste your website: in about two minutes you get your buyers, their decision makers with verified emails and a first email to each.',
      buttonLabel: 'Analyse my website',
      to: '/$lang',
    },
  },
  ru: {
    'context-audit': {
      title: 'Узнайте, где ваша команда теряет контекст',
      body: 'Подключите рабочее пространство и найдите разрозненный, устаревший или недостающий контекст.',
      buttonLabel: 'Запустить Context Audit',
      to: '/$lang/signup',
    },
    'build-agent': {
      title: 'Превратите этот процесс в управляемого AI-агента',
      body: 'Создайте агента с правильными инструкциями, контекстом, инструментами и границами согласования.',
      buttonLabel: 'Создать агента',
      to: '/$lang/signup',
    },
    'book-demo': {
      title: 'Посмотрите, как Laplace встраивается в ваши процессы',
      body: 'Изучите возможности управляемых AI-агентов на примере собственных процессов и систем.',
      buttonLabel: 'Запросить демо',
      to: '/$lang/demo',
    },
    'find-customers': {
      title: 'Посмотрите, кто может у вас купить',
      body: 'Вставьте адрес сайта: примерно за две минуты вы получите покупателей, лиц, принимающих решения, с проверенными email и первое письмо каждому.',
      buttonLabel: 'Проанализировать мой сайт',
      to: '/$lang',
    },
  },
}

type ResourceCtaProps = {
  type: ResourceCtaType
  placement: 'article' | 'final'
  language: ResourceLanguage
}

export function ResourceCta({ type, placement, language }: ResourceCtaProps) {
  const content = CTA_CONTENT[language][type]

  const handleClick = () => {
    const params = {
      source: `resources_${placement}_${type}`,
      button_text: content.buttonLabel,
    }

    if (content.to === '/$lang/demo') {
      trackClickRequestDemo(params)
      return
    }

    trackClickSignup(params)
  }

  return (
    <section
      className={
        placement === 'final'
          ? 'relative overflow-hidden rounded-[1.75rem] border border-border bg-card/85 p-8 sm:p-10 lg:flex lg:items-center lg:justify-between lg:gap-10'
          : 'relative my-10 overflow-hidden rounded-[1.35rem] border border-upgrade-cta/25 bg-upgrade-cta/8 p-6 sm:p-7'
      }
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_120%,var(--marketing-glow-strong),transparent_60%)]" />
      <div className="relative max-w-2xl">
        <h2
          className={
            placement === 'final'
              ? 'text-2xl font-display font-medium tracking-[-0.01em] text-foreground sm:text-3xl'
              : 'text-xl font-semibold tracking-[-0.02em] text-foreground'
          }
        >
          {content.title}
        </h2>
        <p className="mt-3 text-[15px] leading-6 text-muted-foreground">
          {content.body}
        </p>
      </div>
      <Button
        asChild
        size="lg"
        className={cn(
          'relative rounded-xl bg-upgrade-cta px-6 text-upgrade-cta-foreground hover:bg-upgrade-cta/90',
          placement === 'article' ? 'mt-8' : 'mt-6 lg:mt-0',
        )}
      >
        <Link to={content.to} params={{ lang: language }} onClick={handleClick}>
          {content.buttonLabel}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </Button>
    </section>
  )
}
